import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "creating-actions"
    }}>{`Creating Actions`}</h1>
    <p>{`For your code to execute as an `}<em parentName="p">{`action`}</em>{` on Adobe I/O Runtime, your code has to comply with two rules:`}</p>
    <ul>
      <li parentName="ul">{`You either call your function `}<em parentName="li">{`main`}</em>{` or you export the entry point as `}<em parentName="li">{`main`}</em>{`. This is the function that will be executed when it is invoked.`}</li>
      <li parentName="ul">{`Your function accepts valid JSON objects as input and produces valid JSON objects as output, if needed`}</li>
    </ul>
    <p>{`You have to configure `}<strong parentName="p">{`aio CLI`}</strong>{` on your machine to create and invoke actions. Refer to the `}<a parentName="p" {...{
        "href": "../tools/cli_install.md"
      }}>{`aio CLI`}</a>{` page for how to install and configure it. `}</p>
    <p>{`Let`}{`’`}{`s assume you have this function available on your machine:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// this is saved in a file named first-function.js
function main(params) {
    var nm = params.name || 'stranger';
    return {payload: 'Hello ' + nm};
}

exports.main = main;
`}</code></pre>
    <p>{`You can create an action called `}<em parentName="p">{`test`}</em>{` using this command:`}</p>
    <pre><code parentName="pre" {...{}}>{`aio rt:action:create test first-function.js
`}</code></pre>
    <p>{`You can update an action at any time using the following command:`}</p>
    <pre><code parentName="pre" {...{}}>{`aio rt:action:update test first-function.js
`}</code></pre>
    <p>{`If you don`}{`’`}{`t need an action anymore, you can delete it:`}</p>
    <pre><code parentName="pre" {...{}}>{`aio rt:action:delete test
`}</code></pre>
    <p>{`If you want to save an action that is deployed to your machine, then you can use this command:`}</p>
    <pre><code parentName="pre" {...{}}>{`aio rt:action:get test --save 
`}</code></pre>
    <p>{`Listing all the available actions in your current namespace is as simple as running this command:`}</p>
    <pre><code parentName="pre" {...{}}>{`aio rt:action:list
`}</code></pre>
    <h2 {...{
      "id": "invoking-actions"
    }}>{`Invoking actions`}</h2>
    <p>{`Now, that you have an action, you can call it using the following command (in this example the action name is `}<em parentName="p">{`test`}</em>{`):`}</p>
    <pre><code parentName="pre" {...{}}>{`aio rt:action:invoke test --result
`}</code></pre>
    <p>{`Note the flag `}<em parentName="p">{`--result`}</em>{` used in the command. This flag outputs the result of the invocation. Without it, instead of seeing the result of the invocation, you`}{`’`}{`d get the activation ID. To get the result, you`}{`’`}{`d use this ID to retrieve the result like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`aio rt:activation:get <activation ID>
`}</code></pre>
    <p>{`When you invoke an action, as in the example above, the invocation is not blocking (it is async). If you want to execute it in a blocking style and, as a result, get the activation record instead of just getting an activation ID, you have to add the `}<em parentName="p">{`--blocking`}</em>{` flag to the command:`}</p>
    <pre><code parentName="pre" {...{}}>{`aio rt:action:invoke test --blocking
`}</code></pre>
    <h2 {...{
      "id": "working-with-parameters"
    }}>{`Working with parameters`}</h2>
    <p>{`Actions can receive parameters when are being executed. First, any parameters you sent to the action will be available through the `}<em parentName="p">{`params`}</em>{` variable. If you send two parameters called `}<em parentName="p">{`first-name`}</em>{` and `}<em parentName="p">{`last-name`}</em>{`, they will be available as `}<em parentName="p">{`params.first-name`}</em>{` and `}<em parentName="p">{`params.last-name`}</em>{`.`}</p>
    <p>{`Second, let`}{`’`}{`s see how you can invoke the action with parameters. Our function sample from above uses a parameter called `}<em parentName="p">{`name`}</em>{`. This is how you can set the parameter when invoking the action:`}</p>
    <pre><code parentName="pre" {...{}}>{`aio rt:action:invoke test --param name "John Doe" --result
`}</code></pre>
    <h2 {...{
      "id": "setting-default-parameters"
    }}>{`Setting default parameters`}</h2>
    <p>{`Sometimes you want to bind the same parameter values for all invocations or you just want to set default values. In either case, you have two different options: setting params at the package level (so all actions in that package can inherit them) or at action level.`}</p>
    <h3 {...{
      "id": "default-params-and-encryption"
    }}>{`Default params and encryption`}</h3>
    <p>{`Before we dive deeper in how to set and use default params, let’s discuss the security aspect first. Many developers use the default params as a mechanism to provision actions with the secrets/passwords needed to authenticate against some other systems (databases, services, APIs).`}</p>
    <p>{`In order to support this use case, all default params are automatically encrypted. They are decrypted just before the action code is executed. Thus, the only time you have access to the decrypted value is while executing the action code.`}</p>
    <p>{`If you run the CLI command for getting an action or package, you’d get a listing for the names of the default params while the values will be listed as a hash instead of the actual value.`}</p>
    <h3 {...{
      "id": "default-parameters-set-on-action"
    }}>{`Default parameters set on action`}</h3>
    <p>{`Let`}{`’`}{`s assume that you want the default value of your parameter to be "Runtime". You can set this value when creating the action, or if the action already exists, updating the action. In both cases you add the *--param" flag:`}</p>
    <pre><code parentName="pre" {...{}}>{`// creation time
aio rt:action:create test first-function.js --param name "Runtime"

// update
aio rt:action:update test first-function.js --param name "Runtime"
`}</code></pre>
    <p>{`Now, you can run the action without any parameters, and it will use the default one you`}{`’`}{`ve set. If you set a parameter when invoking, this will overwrite the default one.`}</p>
    <blockquote>
      <p parentName="blockquote">{`Important!  When updating an action's params using `}<inlineCode parentName="p">{`--param <key> <value>`}</inlineCode>{` as above, you must specify ALL of the parameters.  All previous values will be overwritten.`}</p>
    </blockquote>
    <h3 {...{
      "id": "default-parameters-set-on-package"
    }}>{`Default parameters set on package`}</h3>
    <p>{`When you create an action, it is always created in a package. If you don`}{`’`}{`t specify a package, the `}<em parentName="p">{`default`}</em>{` package is used. Similar to how you set default parameters at the action level, you can specify default parameters at the package level.`}</p>
    <p>{`The difference is that the params set at the package level will be used for all the actions you create in that package.`}</p>
    <pre><code parentName="pre" {...{}}>{`// creation time
aio rt:package:create my-package --param name "Runtime"

// update
aio rt:package:update my-package --param name "Runtime"
`}</code></pre>
    <p>{`At this point, you might ask yourself what is the precedence when setting parameters at the package level, action level, and invocation time. The winner is parameters set at invocation time, followed by parameters set at action level, and the last is parameters set at package level.`}</p>
    <h3 {...{
      "id": "default-parameters-with-parameter-file"
    }}>{`Default parameters with parameter file`}</h3>
    <p>{`A neat way for setting default params is using a dedicated file for storing their values, and then using this file to set those values as default parameter values.`}</p>
    <p>{`This is especially useful when you are dealing with multiple parameter values for things like configuring API access keys, endpoints, and so on.`}</p>
    <p>{`Coming back to our sample function that expects one parameter, `}<em parentName="p">{`name`}</em>{`, you`}{`’`}{`d create a JSON file to store that value:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`// filename is my-params.json
{
    "name": "Runtime"
}
`}</code></pre>
    <p>{`Then, you use the `}<em parentName="p">{`--param-file`}</em>{` flag when creating actions, creating packages, or invoking actions.`}</p>
    <pre><code parentName="pre" {...{}}>{`// update action
aio rt:action:update test first-function.js --param-file my-params.json

// invoke action
aio rt:action:invoke test --param-file my-params.json
`}</code></pre>
    <h3 {...{
      "id": "final-parameters"
    }}>{`Final Parameters`}</h3>
    <p>{`Sometimes, an application needs to ensure that the default parameters are final (or immutable), and a calling client can`}{`’`}{`t override them. You can achieve this by adding the `}<inlineCode parentName="p">{`final`}</inlineCode>{` annotation - `}<inlineCode parentName="p">{`-a final true`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{}}>{`aio rt:action:update test first-function.js --web true --param name "Runtime" -a final true
`}</code></pre>
    <p>{`This mechanism works for all type of actions including web actions.`}</p>
    <h2 {...{
      "id": "invoking-web-actions"
    }}>{`Invoking web actions`}</h2>
    <p>{`So far, we`}{`’`}{`ve been invoking actions only from the CLI. While this might be good for trying out your actions, as you design actions for production systems, you might need to be able to invoke actions via HTTP REST calls. This would enable you to invoke the actions from your web application. `}</p>
    <p>{`You create a web action by adding the `}<em parentName="p">{`--web`}</em>{` flag to the `}<inlineCode parentName="p">{`aio`}</inlineCode>{` action command:`}</p>
    <pre><code parentName="pre" {...{}}>{`// creation time
aio rt:action:create test first-function.js --web true

// update
aio rt:action:update test first-function.js --web true
`}</code></pre>
    <p>{`Notice the `}<em parentName="p">{`true`}</em>{` value used in the command. If you set that value to `}<em parentName="p">{`false`}</em>{`, then you disable a web action.`}</p>
    <p>{`To call the action as a web action, you need to know the full path to the action. You can find the path by adding the `}<em parentName="p">{`--url`}</em>{` flag to the action command:`}</p>
    <pre><code parentName="pre" {...{}}>{`aio rt:action:get test --url
`}</code></pre>
    <p>{`This will give you something like:`}</p>
    <pre><code parentName="pre" {...{}}>{`ok: got action test
https://adobeioruntime.net/api/v1/web/[your namespace]/default/test
`}</code></pre>
    <p>{`In the URL above, the `}<inlineCode parentName="p">{`default`}</inlineCode>{` in the path stands for the `}<inlineCode parentName="p">{`default`}</inlineCode>{` package: if you don`}{`’`}{`t create your actions explicitly in a package, then they get under the `}<inlineCode parentName="p">{`default`}</inlineCode>{` package.`}</p>
    <p>{`You can invoke the action like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`curl -L https://adobeioruntime.net/api/v1/web/[your namespace]/default/test -X GET
`}</code></pre>
    <p>{`or`}</p>
    <pre><code parentName="pre" {...{}}>{`curl https://[your namespace].adobeioruntime.net/api/v1/web/default/test -X GET
`}</code></pre>
    <p><strong parentName="p">{`Note`}</strong>{` the change in the URL here in comparison to what the `}<inlineCode parentName="p">{`aio`}</inlineCode>{` returns. This is due some additional protections Runtime provides to segregate namespaces from each other when invoking web actions. The `}<inlineCode parentName="p">{`aio`}</inlineCode>{` generated link will still work but it will return a 308 redirect to your namespace's subdomain on Runtime. For a further discussion of this please see the `}<a parentName="p" {...{
        "href": "securing_web_actions.md"
      }}>{`Securing Web Actions`}</a>{` page.`}</p>
    <h3 {...{
      "id": "successful-response"
    }}>{`Successful response`}</h3>
    <p>{`When creating actions to be used as web actions, you might want to send the response that follows the HTTP response structure (status code, headers, body). For example, our sample function could be rewritten:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function main(params) {
    var nm = params.name || 'stranger';
    return {
        statusCode: 200,
        body: {
            payload: 'Hello ' + nm
        }
    }
}

exports.main = main;
`}</code></pre>
    <p>{`You can also set cookies or cache control headers, perform a HTTP redirect, and so forth.`}</p>
    <h3 {...{
      "id": "unsuccessful-response"
    }}>{`Unsuccessful response`}</h3>
    <p>{`On failed action invocations, the error code and message should be wrapped in an `}<inlineCode parentName="p">{`error`}</inlineCode>{` object, as this would allow the system to interpret the response as an `}<inlineCode parentName="p">{`applicationError`}</inlineCode>{`. `}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`async function main(params) {
    try {
        throw new Error("Boom!")
    } catch (err) {
        return {
            error: {
                statusCode: 500,
                body: {
                    payload: \`Something went wrong: \${err}\`
                }
            }
        }
    }
}

exports.main = main;
`}</code></pre>
    <h3 {...{
      "id": "dealing-with-errors-in-general"
    }}>{`Dealing with errors in general`}</h3>
    <p>{`It is extremely important to handle errors in your action code. It is actually impossible to recover from an unhandled async error in Node.js. Please refer to the Node.js documentation related to `}<a parentName="p" {...{
        "href": "https://nodejs.org/api/process.html#event-uncaughtexception"
      }}>{`uncaughtExceptions`}</a>{`.`}</p>
    <p>{`In the event of an unhandled async errors, the action will be terminated and the container running that action will be destroyed. This means that all in flight activations will be failed and, the next invocation of the action will incur the overhead of creating a new container.`}</p>
    <p>{`The following examples will show incorrect and correct handling of async errors:`}</p>
    <h4 {...{
      "id": "incorrect-handling-of-async-errors"
    }}>{`Incorrect handling of async errors`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function doSomethingAsync() {
    return new Promise((resolve, reject) => {
        try {
            setTimeout(() => {
                new Error("Something went wrong");
            }, 1000);
        } catch (err) {
            reject(err);
        }
    });
}
`}</code></pre>
    <p>{`This code will fail to handle the error and the action will be terminated; here the error is generated not while the executor is running, but later. So the try catch block will not be able to catch the error. `}</p>
    <h4 {...{
      "id": "correct-handling-of-async-errors"
    }}>{`Correct handling of async errors`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function doSomethingAsync() {
    return new Promise((resolve, reject) => {
        setTimeout(() => {
            reject(new Error("Something went wrong"));
        }, 1000);
    });
}
`}</code></pre>
    <p>{`This code will execute correctly and the error will be handled.     `}</p>
    <p>{`In general when an asynchronous operation is performed, there is a chance that something could go wrong, such as a network error, a database connection issue, or an unexpected input.
If an error occurs but is not handled, the node process will be terminated. Therefore, it is important to always handle errors inside the callback function passed to setTimeout or any other async function to ensure the reliability and stability of the program.`}</p>
    <h3 {...{
      "id": "http-context"
    }}>{`HTTP context`}</h3>
    <p>{`When executing an action as a web action, the `}<inlineCode parentName="p">{`params`}</inlineCode>{` object is decorated with additional information:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Method`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`__ow_method`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`the HTTP method of the request`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`__ow_headers`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`the request headers`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`__ow_path`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`the unmatched path of the request (matching stops after consuming the action extension)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`__ow_body`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`the request body entity, as a base64-encoded string when its content is binary or JSON object/array, or as a plain string otherwise`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`__ow_query`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`the query parameters from the request as an unparsed string`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`By default, web actions are accessible to anyone who knows the URL. If you want to secure the access, you can find more info on the `}<a parentName="p" {...{
        "href": "securing_web_actions.md"
      }}>{`Securing Web Actions`}</a>{` page.`}</p>
    <h2 {...{
      "id": "deploying-zip-actions"
    }}>{`Deploying ZIP actions`}</h2>
    <p>{`So far, we`}{`’`}{`ve been creating actions from a single source file. What if you need to create an action from multiple files and you also need some `}<strong parentName="p">{`npm`}</strong>{` modules? The answer is deploying a ZIP action. `}</p>
    <p>{`You need to do three things to deploy a ZIP action: create a manifest file, create the package.json file, and configure/use `}<inlineCode parentName="p">{`wskdeploy`}</inlineCode>{` instead of `}<inlineCode parentName="p">{`aio`}</inlineCode>{` for the actual deploy.`}</p>
    <h3 {...{
      "id": "wskdeploy"
    }}>{`wskdeploy`}</h3>
    <p>{` If you need to install `}<inlineCode parentName="p">{`wskdeploy`}</inlineCode>{`, check the page `}<a parentName="p" {...{
        "href": "../tools/wskdeploy_install.md"
      }}>{`Setting up the wskdeploy CLI`}</a>{`.`}</p>
    <h3 {...{
      "id": "packagejson-file"
    }}>{`Package.json file`}</h3>
    <p>{`You use the package.json file to specify the dependencies. If you don`}{`’`}{`t have one already, in the same folder where your function files are, run `}<inlineCode parentName="p">{`npm init -y`}</inlineCode>{`.`}</p>
    <p>{`Make sure you have the npm dependencies you need for your functions declared in the package file.`}</p>
    <h3 {...{
      "id": "wskdeploy-manifest-file"
    }}>{`wskdeploy manifest file`}</h3>
    <p>{`The last step is creating the manifest file that will be used by wskdeploy to create your action. In the parent folder of the folder where you have your function files and npm modules, create a file `}<inlineCode parentName="p">{`manifest.yaml`}</inlineCode>{`. (For more information on YAML format, see the `}<a parentName="p" {...{
        "href": "http://yaml.org/refcard.html"
      }}>{`YAML reference card`}</a>{`.)`}</p>
    <p>{`This is the folder structure:`}</p>
    <pre><code parentName="pre" {...{}}>{`--/actions
--/actions/node_modules/
--/actions/index.js
--/actions/package.json
--manifest.yaml
`}</code></pre>
    <p>{`Here is an example manifest.yaml:`}</p>
    <pre><code parentName="pre" {...{}}>{`packages:
    # this is the package name
    test:
        actions:
            # name of the action
            test-zip:
                # source for the action; in this case it is a folder
                function: actions
                runtime: nodejs:10
                # publish the action as a web action
                web:  yes
`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Note:`}</strong>{` If your action require dependencies (such as other node modules or JavaScript files), you need to place the action code in a `}<inlineCode parentName="p">{`index.js`}</inlineCode>{` file and in the `}<inlineCode parentName="p">{`manifest.yaml`}</inlineCode>{` file, the `}<inlineCode parentName="p">{`function`}</inlineCode>{` value should point to the folder where the action is stored instead of the action file. You can see this in action in the example above.`}</p>
    </blockquote>
    <p>{`Now you are ready to deploy by running the `}<inlineCode parentName="p">{`wskdeploy`}</inlineCode>{` command from the same folder where `}<inlineCode parentName="p">{`manifest.yaml`}</inlineCode>{` is:`}</p>
    <pre><code parentName="pre" {...{}}>{`wskdeploy
`}</code></pre>
    <p>{`This should deploy an action called `}<em parentName="p">{`test-zip`}</em>{` under a package called `}<em parentName="p">{`test`}</em>{`. You can invoke it like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`aio rt:action:invoke test/test-zip --result
`}</code></pre>
    <p>{`If you want to remove this action, you run `}<inlineCode parentName="p">{`wskdeploy`}</inlineCode>{` with the `}<em parentName="p">{`undeploy`}</em>{` flag:`}</p>
    <pre><code parentName="pre" {...{}}>{`wskdeploy undeploy
`}</code></pre>
    <p>{`There are other useful flows `}<inlineCode parentName="p">{`wskdeploy`}</inlineCode>{` supports. Please check the `}<a parentName="p" {...{
        "href": "https://github.com/apache/openwhisk-wskdeploy"
      }}>{`official documentation`}</a>{` if you want to find more.`}</p>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Note:`}</strong>{` There is another way of deploying a ZIP action using the aio command. You miss the manifest.yaml file flexibility with this mode; you can`}{`’`}{`t define multiple actions/packages at the same time. The ZIP file has to have in the root the package.json file.`}<br parentName="p"></br>{`
`}<inlineCode parentName="p">{`aio rt:action:create my-action --kind nodejs:20 zip-file.zip`}</inlineCode></p>
    </blockquote>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      